import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import type { DraftConfig, EventSection } from "@/lib/types/config";
import type { EventDetail } from "@/lib/types/api";

/**
 * Where an edit is.
 *
 *   dirty     typed, not sent yet
 *   saving    in flight
 *   live      applied — a guest would see this now
 *   rejected  validation refused it; the booth kept the last good config
 *   error     the request itself failed
 */
export type SaveState = "idle" | "dirty" | "saving" | "live" | "rejected" | "error";

interface BuilderContextValue {
  event: EventDetail;
  draft: DraftConfig;
  saveState: SaveState;
  /** Bumped whenever a save lands, so the preview iframe knows to refetch. */
  previewVersion: number;
  update: (mutate: (draft: DraftConfig) => DraftConfig) => void;
  patchEvent: (patch: Partial<EventSection>) => void;
  setEvent: (event: EventDetail) => void;
  markPreviewStale: () => void;
}

const BuilderContext = createContext<BuilderContextValue | null>(null);

export function BuilderProvider({
  event, draft, saveState, previewVersion, onDraftChange, onEventChange, onPreviewStale, children,
}: {
  event: EventDetail;
  draft: DraftConfig;
  saveState: SaveState;
  previewVersion: number;
  onDraftChange: (next: DraftConfig) => void;
  onEventChange: (event: EventDetail) => void;
  onPreviewStale: () => void;
  children: ReactNode;
}) {
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const update = useCallback((mutate: (current: DraftConfig) => DraftConfig) => {
    onDraftChange(mutate(draftRef.current));
  }, [onDraftChange]);

  // Nearly every tab edits something under `event`, so this shorthand keeps the
  // deep-merge boilerplate out of the tab components.
  const patchEvent = useCallback((patch: Partial<EventSection>) => {
    onDraftChange({
      ...draftRef.current,
      event: { ...draftRef.current.event, ...patch },
    });
  }, [onDraftChange]);

  const value = useMemo<BuilderContextValue>(() => ({
    event,
    draft,
    saveState,
    previewVersion,
    update,
    patchEvent,
    setEvent: onEventChange,
    markPreviewStale: onPreviewStale,
  }), [event, draft, saveState, previewVersion, update, patchEvent, onEventChange, onPreviewStale]);

  return <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>;
}

export function useBuilder(): BuilderContextValue {
  const context = useContext(BuilderContext);
  if (!context) throw new Error("useBuilder must be used inside a BuilderProvider");
  return context;
}

/** Convenience reader with sensible fallbacks for the deeply nested branding tree. */
export function useBranding() {
  const { draft, update } = useBuilder();
  const branding = draft.event?.branding ?? {};

  const setBranding = useCallback((patch: Partial<typeof branding>) => {
    update((current) => ({
      ...current,
      event: {
        ...current.event,
        branding: { ...current.event?.branding, ...patch },
      },
    }));
  }, [update]);

  return { branding, setBranding };
}

export function useSaveIndicator(state: SaveState): { label: string; tone: string } {
  switch (state) {
    case "saving": return { label: "Saving…", tone: "text-haze-400" };
    case "live": return { label: "Live", tone: "text-ok" };
    case "rejected": return { label: "Not applied — see errors", tone: "text-danger" };
    case "dirty": return { label: "Saving…", tone: "text-warn" };
    case "error": return { label: "Save failed", tone: "text-danger" };
    default: return { label: "Live", tone: "text-haze-400" };
  }
}
